import { TweenMax, TimelineLite } from 'gsap';
import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock';
import * as focusTrap from 'focus-trap';

let menuItems = null;
let menu = null;
let menuInner = null;
let main = null;
let openMenuBtn = null;
let openSearchBtns = null;
let closeBtn = null;
// Matches $breakpoints.lg in resources/sass/00_settings/_breakpoints.scss — the point
// where the nav switches from mobile drawer (click-driven) to desktop dropdown (hover-driven).
const hoverBreakpoint = 1200;
let trap = null;
let currentSubmenuTrap = null;
let openSubLinks = null;
let closeSubLinks = null;
let subMenuContainers = null;

const Navigation = {
    init() {
        menu = document.querySelector('.nav-primary');
        menuInner = document.querySelector('.nav-primary__inner');
        main = document.querySelector('.main');
        trap = focusTrap.createFocusTrap(menuInner, { escapeDeactivates: false });
        subMenuContainers = document.querySelectorAll('.nav-primary__sublist-container');

        if (menu !== null) {
            menuItems = [...menu.querySelectorAll('.nav-primary__item')];
            openMenuBtn = menu.querySelector('.js-nav-primary-open-menu');
            openSearchBtns = [...menu.querySelectorAll('.js-nav-primary-open-search')];
            closeBtn = menu.querySelector('.js-nav-primary-close');

            openSubLinks = [...menu.querySelectorAll('.js-open-submenu')];
            closeSubLinks = [...menu.querySelectorAll('.js-close-submenu')];

            if (openSubLinks) {
                openSubLinks.forEach(button => {
                    button.addEventListener('click', this.openSubMenu); // click on mobile

                    // Hover only drives the submenu on desktop. On mobile, touch interaction can
                    // fire delayed synthetic mouseenter/mouseleave events that would otherwise
                    // toggle aria-expanded on the button underneath the closing submenu overlay.
                    button.addEventListener('mouseenter', e => {
                        if (window.innerWidth >= hoverBreakpoint) this.menuItemWithSubMouseEnter(e);
                    });
                    button.addEventListener('mouseleave', e => {
                        if (window.innerWidth >= hoverBreakpoint) this.menuItemWithSubMouseLeave(e);
                    });
                    button.addEventListener('blur', this.menuItemWithSubMouseLeave.bind(this));

                    // Close the submenu when the mouse leaves the submenu container (desktop only)
                    subMenuContainers.forEach(container => {
                        // Keep dropdown open while pointer traverses the gap between nav link and panel
                        container.addEventListener('mouseenter', () => {
                            if (window.innerWidth < hoverBreakpoint) return;
                            clearTimeout(this.subLeaveTimeout);
                        });
                        container.addEventListener('mouseleave', () => {
                            if (window.innerWidth < hoverBreakpoint) return;
                            document.body.classList.remove('nav-primary-sublist--is-open');
                            if (this.currentSubOpen) {
                                this.currentSubOpen.ariaExpanded = false;
                                this.currentSubOpen.classList.remove('nav-primary-sublist--is-open');
                                this.currentSubOpen = null;
                            }
                        });
                    });

                    // If focus changes on the children of the item (links in the submenu), close the submenu
                    const subLinks = button
                        .closest('.nav-primary__item')
                        .querySelector('.nav-primary__sublist')
                        .querySelectorAll('a.nav-primary__sublink, button.nav-primary__sublink');

                    subLinks.forEach(subLink => {
                        subLink.addEventListener('focus', () => clearTimeout(this.subLeaveTimeout));
                        subLink.addEventListener('blur', this.menuItemWithSubMouseLeave.bind(this));
                    });
                });
            }

            if (closeSubLinks) {
                closeSubLinks.forEach(subLink => {
                    subLink.addEventListener('click', this.closeSubMenu);
                });
            }

            openMenuBtn.addEventListener('click', this.openAnimation);
            closeBtn.addEventListener('click', this.closeAnimation);
        }

        window.addEventListener('keydown', e => {
            if (e.key !== 'Escape') return;

            if (this.currentSubOpen) {
                this.closeAllSubmenus(e);
            } else if (document.body.classList.contains('nav-primary--is-open')) {
                this.closeAnimation(e);
            }
        });
    },

    currentSubOpen: null,
    subLeaveTimeout: null,

    menuItemWithSubMouseEnter(e) {
        if (this.currentSubOpen) {
            if (this.currentSubOpen !== e.target) {
                this.currentSubOpen.classList.remove('nav-primary-sublist--is-open');
            } else {
                clearTimeout(this.subLeaveTimeout);
            }
        }

        document.body.classList.add('nav-primary-sublist--is-open');
        e.target.classList.add('nav-primary-sublist--is-open');
        e.target.ariaExpanded = true;
        this.currentSubOpen = e.target;
    },

    menuItemWithSubMouseLeave(e) {
        // If parent <li> is not hovered, AND if parent <li> has no focus-within (focus is not in the submenu)
        if (!e.target.closest('li.nav-primary__item:hover, li.nav-primary__item:focus-within')) {
            clearTimeout(this.subLeaveTimeout);
            const triggerToClose = this.currentSubOpen;
            this.subLeaveTimeout = setTimeout(() => {
                document.body.classList.remove('nav-primary-sublist--is-open');
                e.target.classList.remove('nav-primary-sublist--is-open');
                const navItem = e.target.closest('.nav-primary__item');
                const openTrigger = navItem && navItem.querySelector('.nav-primary-sublist--is-open');
                if (openTrigger) openTrigger.classList.remove('nav-primary-sublist--is-open');
                if (triggerToClose) triggerToClose.ariaExpanded = false;
                // 600ms grace period so the dropdown stays reachable (WCAG 2.2 SC 1.4.13, hoverable)
            }, 600);
        }
    },

    openAnimation(e) {
        e.preventDefault();

        if (main && window.innerWidth < hoverBreakpoint) {
            main.style.paddingTop = `${menu.offsetHeight}px`;
        }

        document.body.classList.add('nav-primary--is-open');
        Navigation.prepareAnimation();

        TweenMax.set(closeBtn, { display: 'block' });
        TweenMax.set(openMenuBtn, { display: 'none' });
        if (openSearchBtns && openSearchBtns.length) {
            TweenMax.set(openSearchBtns, { display: 'none' });
        }

        menuItems.forEach((item, index) => {
            TweenMax.to(item.closest('li.nav-primary__item'), 0.75, { opacity: 1, delay: 0.1 + index * 0.05 });
        });

        disableBodyScroll(document.body);
        trap.activate();
    },

    closeAnimation(e) {
        e.preventDefault();
        trap.deactivate();

        const tl = new TimelineLite({ onComplete: Navigation.reverseAnimation });
        tl.to(openMenuBtn, 0, { display: 'block' });
        if (openSearchBtns && openSearchBtns.length) {
            tl.to(openSearchBtns, 0, { display: 'none' });
        }
        tl.to(closeBtn, 0, { display: 'none' }).to(menuItems, 0.5, { opacity: 0 }, '-=0.60');
    },

    prepareAnimation() {
        TweenMax.set(menuItems, {
            opacity: 0,
        });
    },

    reverseAnimation() {
        Navigation.resetAnimation();
    },

    resetAnimation() {
        if (menu) {
            enableBodyScroll(document.body);
            document.body.classList.remove('nav-primary--is-open');

            if (main) main.style.paddingTop = '';

            if (window.isMobileLayout) {
                TweenMax.set(openMenuBtn, { clearProps: 'display' });
                if (openSearchBtns && openSearchBtns.length) {
                    TweenMax.set(openSearchBtns, { clearProps: 'display' });
                }
                TweenMax.set(closeBtn, { display: 'none' });
            } else {
                TweenMax.set(openMenuBtn, { clearProps: 'display' });
                if (openSearchBtns && openSearchBtns.length) {
                    TweenMax.set(openSearchBtns, { clearProps: 'display' });
                }
                TweenMax.set(openSubLinks, { opacity: 1 });
            }
        }
    },

    openSubMenu(e) {
        const toggle = e.currentTarget;
        const navItem = toggle.closest('.nav-primary__item');

        const alreadyOpen =
            navItem.classList.contains('nav-primary__item--is-open') ||
            toggle.classList.contains('nav-primary-sublist--is-open');

        if (e.detail === 0 && alreadyOpen) {
            Navigation.closeSubMenu.call(toggle);
            return;
        }

        if (Navigation.currentSubOpen && Navigation.currentSubOpen !== toggle) {
            Navigation.closeSubMenu.call(Navigation.currentSubOpen);
        }

        document.body.classList.add('nav-primary-sublist--is-open');
        toggle.classList.add('nav-primary-sublist--is-open');
        navItem.classList.add('nav-primary__item--is-open');
        toggle.ariaExpanded = true;
        Navigation.currentSubOpen = toggle;

        if (window.innerWidth < hoverBreakpoint) {
            currentSubmenuTrap = focusTrap.createFocusTrap(navItem.querySelector('.nav-primary__sublist-container'), {
                escapeDeactivates: false,
            });
            currentSubmenuTrap.activate();
        }

        if (e.detail === 0) {
            const firstSubLink = navItem.querySelector('.nav-primary__sublist a');
            if (firstSubLink) firstSubLink.focus();
        }
    },

    closeSubMenu() {
        if (currentSubmenuTrap) currentSubmenuTrap.deactivate();
        document.body.classList.remove('nav-primary-sublist--is-open');
        const navItem = this.closest('.nav-primary__item');
        navItem.classList.remove('nav-primary__item--is-open');
        const toggle = navItem.querySelector('.js-open-submenu');
        if (toggle) {
            toggle.ariaExpanded = false;
            toggle.classList.remove('nav-primary-sublist--is-open');
        }
        if (Navigation.currentSubOpen === toggle) {
            Navigation.currentSubOpen = null;
        }
    },

    closeAllSubmenus() {
        if (currentSubmenuTrap) currentSubmenuTrap.deactivate();
        document.body.classList.remove('nav-primary-sublist--is-open');

        // Escape hides the sublink (display:none), so return focus to its trigger, not <body>.
        const openTrigger = this.currentSubOpen;
        const navItem = openTrigger && openTrigger.closest('.nav-primary__item');
        const focusWasInSubmenu = navItem && navItem.contains(document.activeElement);

        if (this.currentSubOpen) {
            this.currentSubOpen.ariaExpanded = false;
            this.currentSubOpen = null;
        }

        // Find all elements with "open" classes, and remove those classes
        const elsWithIsOpenClass = document.querySelectorAll(
            '.nav-primary-sublist--is-open, .nav-primary__item--is-open'
        );

        for (let i = 0; i < elsWithIsOpenClass.length; i += 1) {
            const cur = elsWithIsOpenClass[i];
            cur.classList.remove('nav-primary-sublist--is-open', 'nav-primary__item--is-open');
        }

        if (openTrigger && focusWasInSubmenu) openTrigger.focus();
    },
};

window.addEventListener('resize', () => {
    Navigation.resetAnimation();
});

export default Navigation;
